import React from 'react';

import { useQuery } from '@tanstack/react-query';

import { Box, Link, Typography } from '@mui/material';

import { getOrder } from '~/services/OrdersService';
import { getGiftCards } from '~/services/PromoService';
import { getTimeDifference } from '~/services/TimeService';

import { Payment } from '~/types/services/payment';

import { PaymentPlanAdditionalInfo } from './PaymentPlanAdditionalInfo';
import { getGiftCardPaymentLink } from './giftCardPaymentLink';

interface Props {
  payment: Payment;
  paymentPlanComponent?: App.PaymentPlanComponent;
}

function GiftCardPaymentLine({ payment, paymentPlanComponent }: Props) {
  const giftCardCode =
    (payment.response_json.result as { giftCard?: { gift_card_code?: string } })?.giftCard?.gift_card_code || '';
  const { link, isPhysicalCard } = getGiftCardPaymentLink(giftCardCode);

  // Fetch gift card details using the API (only for non-physical cards)
  const { data: giftCardData } = useQuery({
    queryKey: ['giftCard', giftCardCode, payment.brand],
    queryFn: async () => {
      if (!giftCardCode) {
        return null;
      }
      return await getGiftCards({
        code: giftCardCode,
        pageNum: 0,
        pageSize: 1,
        brand: payment.brand,
      });
    },
    enabled: !!giftCardCode && !isPhysicalCard,
  });

  const giftCardLog = giftCardData?.result?.logs?.[0];

  // Fetch original order details to get gift card message
  const { data: originalOrder } = useQuery({
    queryKey: ['originalOrder', giftCardLog?.fk_order_id],
    queryFn: async () => {
      if (!giftCardLog?.fk_order_id) {
        return null;
      }
      return await getOrder(giftCardLog.fk_order_id);
    },
    enabled: !!giftCardLog?.fk_order_id,
  });

  // Extract gift card message from original order
  const getGiftCardMessage = () => {
    if (!originalOrder?.result) {
      return null;
    }

    const order = originalOrder.result;
    const giftCardItem = order.gift_card_items?.[0];

    if (!giftCardItem?.personalised) {
      return null;
    }

    const { to, from, message, is_blank_message } = giftCardItem.personalised;

    if (is_blank_message) {
      return 'Blank message on card';
    }

    if (!message) {
      return null;
    }

    return `To ${to || 'Unknown'}: "${message}" From ${from || 'Unknown'}`;
  };

  const giftCardMessage = getGiftCardMessage();

  // Calculate time difference between gift card creation and payment usage
  const getGiftCardUsageTimeDifference = () => {
    if (!giftCardLog?.created_at || !payment.created_at) {
      return null;
    }

    const giftCardCreatedDate = new Date(giftCardLog.created_at);
    const paymentDate = new Date(payment.created_at);

    // Calculate hours difference to determine if we should include hours/minutes/seconds
    const hoursDiff = Math.abs(paymentDate.getTime() - giftCardCreatedDate.getTime()) / (1000 * 60 * 60);
    const includeHoursMinsSecs = hoursDiff < 25;

    return getTimeDifference(paymentDate, giftCardCreatedDate, includeHoursMinsSecs, true);
  };

  const timeDifference = getGiftCardUsageTimeDifference();

  return (
    <Box>
      {Math.abs(Number(payment.amount)).toLocaleString('en-US', { minimumFractionDigits: 0 })}{' '}
      <Link href={link} target="_blank" rel="noopener noreferrer">
        {payment.type}
      </Link>{' '}
      <PaymentPlanAdditionalInfo paymentPlanComponent={paymentPlanComponent} />{' '}
      {payment.status !== 'completed' && payment.status}
      {(isPhysicalCard || giftCardLog) && (
        <>
          <br />
          <Typography variant="caption" color="text.secondary">
            {isPhysicalCard ? (
              <>Physical store card</>
            ) : timeDifference ? (
              <>
                Redeemed {timeDifference} after the gift card was created by{' '}
                {giftCardLog.added_by === payment.fk_customer ? (
                  <>themselves</>
                ) : (
                  <Link href={`/users/${giftCardLog.added_by}`} target="_blank" rel="noopener noreferrer">
                    another user
                  </Link>
                )}
              </>
            ) : (
              <>Redemption time unknown</>
            )}
          </Typography>
          {giftCardMessage && (
            <>
              <br />
              <Typography variant="caption" color="text.secondary">
                {giftCardMessage}
              </Typography>
            </>
          )}
        </>
      )}
    </Box>
  );
}

export { GiftCardPaymentLine };
